import { z } from "zod";
import { computePreviewVersionId } from "../hashing/preview-version-id.js";
import { AgencyIdSchema, ClientLinkIdSchema, ProjectIdSchema } from "../primitives/ids.js";
/** client-link.v1.0 lifecycle — Constitution v1.1 / Tier 1 Step 3. */
export const ClientLinkStatusSchema = z.enum([
    "INTAKE_OPEN",
    "INTAKE_IN_PROGRESS",
    "INTAKE_SUBMITTED",
    "PREVIEW_READY",
    "CHANGE_REQUESTED",
    "APPROVAL_PENDING",
    "APPROVED",
    "PORTAL_ACTIVE",
    "SAFE_EDIT_ACTIVE",
    "HANDOFF_READY",
    "EXPIRED",
    "REVOKED",
]);
/** Terminal client-link states — no further transitions are permitted. */
export const CLIENT_LINK_TERMINAL_STATUSES = ["EXPIRED", "REVOKED"];
/**
 * client-link.v1.0 state machine (Lane F / Phase 2).
 * Single source of truth for allowed ClientLink lifecycle transitions. Mirrors the
 * generation-run transition guard. EXPIRED/REVOKED are terminal and reachable from any
 * non-terminal state (link can be expired or revoked at any point in its lifecycle).
 */
const CLIENT_LINK_ALLOWED_TRANSITIONS = {
    INTAKE_OPEN: ["INTAKE_IN_PROGRESS", "INTAKE_SUBMITTED", "PREVIEW_READY"],
    INTAKE_IN_PROGRESS: ["INTAKE_SUBMITTED", "PREVIEW_READY"],
    INTAKE_SUBMITTED: ["PREVIEW_READY"],
    PREVIEW_READY: ["APPROVAL_PENDING", "CHANGE_REQUESTED", "APPROVED"],
    CHANGE_REQUESTED: ["PREVIEW_READY", "APPROVAL_PENDING", "APPROVED"],
    APPROVAL_PENDING: ["APPROVED", "CHANGE_REQUESTED"],
    APPROVED: ["PORTAL_ACTIVE", "SAFE_EDIT_ACTIVE", "HANDOFF_READY"],
    PORTAL_ACTIVE: ["SAFE_EDIT_ACTIVE", "HANDOFF_READY"],
    SAFE_EDIT_ACTIVE: ["PORTAL_ACTIVE", "PREVIEW_READY", "HANDOFF_READY"],
    HANDOFF_READY: ["PORTAL_ACTIVE"],
};
export function canTransitionClientLinkStatus(from, to) {
    if (from === to) {
        return true;
    }
    if (CLIENT_LINK_TERMINAL_STATUSES.includes(from)) {
        return false;
    }
    // A live link may always be expired or revoked.
    if (to === "EXPIRED" || to === "REVOKED") {
        return true;
    }
    return CLIENT_LINK_ALLOWED_TRANSITIONS[from]?.includes(to) ?? false;
}
export function assertClientLinkTransition(from, to) {
    if (!canTransitionClientLinkStatus(from, to)) {
        throw new Error(`Invalid client-link transition: ${from} → ${to}`);
    }
}
export const ClientLinkV1Schema = z
    .object({
    schemaVersion: z.literal("client-link.v1.0"),
    agencyId: AgencyIdSchema,
    clientLinkId: ClientLinkIdSchema,
    projectId: ProjectIdSchema,
    status: ClientLinkStatusSchema,
    allowedDomains: z.array(z.string().min(1)).default([]),
    canonicalDomain: z.string().min(1).optional(),
    expiresAt: z.string().datetime().nullable().default(null),
    revokedAt: z.string().datetime().nullable().default(null),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const ClientLinkCreateRequestV1Schema = z
    .object({
    schemaId: z.literal("client-link.createRequest.v1"),
    status: ClientLinkStatusSchema.default("INTAKE_OPEN"),
    canonicalDomain: z.string().min(1).optional(),
    allowedDomains: z.array(z.string().min(1)).max(10).default([]),
    expiresAt: z.string().datetime().nullable().optional(),
    clientDisplayName: z.string().min(1).max(160).optional(),
})
    .strict();
export const ClientLinkCreateResultV1Schema = z
    .object({
    schemaId: z.literal("client-link.createResult.v1"),
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    clientLinkId: ClientLinkIdSchema,
    status: ClientLinkStatusSchema,
    /** Returned only at creation time. Store only tokenHash server-side. */
    token: z.string().min(32),
    shellPath: z.string().min(1),
    expiresAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
})
    .strict();
/** Agency reissue of an existing project client-link token (mid-journey recovery). Token returned once. */
export const ClientLinkReissueRequestV1Schema = z
    .object({
    schemaId: z.literal("client-link.reissueRequest.v1"),
})
    .strict();
export const ClientLinkReissueResultV1Schema = z
    .object({
    schemaId: z.literal("client-link.reissueResult.v1"),
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    clientLinkId: ClientLinkIdSchema,
    status: ClientLinkStatusSchema,
    /** Returned only at reissue time. Store only tokenHash server-side. */
    token: z.string().min(32),
    shellPath: z.string().min(1),
    expiresAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    reissuedAt: z.string().datetime(),
})
    .strict();
/** Agency opens the post-handoff ongoing-care / client-safe-edit window. */
export const ClientLinkOpenOngoingCareRequestV1Schema = z
    .object({
    schemaId: z.literal("client-link.openOngoingCareRequest.v1"),
    clientLinkId: ClientLinkIdSchema.optional(),
})
    .strict();
export const ClientLinkOpenOngoingCareResultV1Schema = z
    .object({
    schemaId: z.literal("client-link.openOngoingCareResult.v1"),
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    clientLinkId: ClientLinkIdSchema,
    fromStatus: ClientLinkStatusSchema,
    status: z.literal("SAFE_EDIT_ACTIVE"),
    updatedAt: z.string().datetime(),
})
    .strict();
/** Agency authenticates a revoke of a live client-link. */
export const ClientLinkRevokeRequestV1Schema = z
    .object({
    schemaId: z.literal("client-link.revokeRequest.v1"),
    clientLinkId: ClientLinkIdSchema.optional(),
})
    .strict();
export const ClientLinkRevokeResultV1Schema = z
    .object({
    schemaId: z.literal("client-link.revokeResult.v1"),
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    clientLinkId: ClientLinkIdSchema,
    status: z.literal("REVOKED"),
    revokedAt: z.string().datetime(),
})
    .strict();
export const ClientLinkSessionCreateRequestV1Schema = z
    .object({
    schemaId: z.literal("client-link.sessionCreateRequest.v1"),
    token: z.string().min(32),
})
    .strict();
export const ClientLinkSessionCreateResultV1Schema = z
    .object({
    schemaId: z.literal("client-link.sessionCreateResult.v1"),
    clientLinkId: ClientLinkIdSchema,
    sessionToken: z.string().min(32),
    expiresAt: z.string().datetime(),
})
    .strict();
export const ClientLinkStateV1Schema = z
    .object({
    schemaId: z.literal("client-link.state.v1"),
    clientLinkId: ClientLinkIdSchema,
    status: ClientLinkStatusSchema,
    expiresAt: z.string().datetime().nullable(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const ClientLinkAccessContextV1Schema = z
    .object({
    schemaId: z.literal("client-link.accessContext.v1"),
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    clientLinkId: ClientLinkIdSchema,
    status: ClientLinkStatusSchema,
    allowedDomains: z.array(z.string().min(1)),
    canonicalDomain: z.string().min(1).nullable(),
})
    .strict();
export const ClientPortalShellSectionStateV1Schema = z.enum(["AVAILABLE", "COMING_SOON", "LOCKED"]);
export const ClientPortalShellSectionV1Schema = z
    .object({
    id: z.enum([
        "intake",
        "preview",
        "approval",
        "safe_edit",
        "reports",
        "handoff",
        "governance",
        "external_proof_review",
    ]),
    label: z.string().min(1),
    description: z.string().min(1),
    state: ClientPortalShellSectionStateV1Schema,
})
    .strict();
export const ClientSafeProjectViewV1Schema = z
    .object({
    schemaId: z.literal("client-safe-project-view.v1"),
    displayName: z.string().min(1),
    lifecycleStatus: ClientLinkStatusSchema,
})
    .strict();
const ClientHubPrimaryColorSchema = z
    .string()
    .nullable()
    .optional()
    .transform((value) => (value && /^#[0-9A-Fa-f]{6}$/.test(value) ? value : undefined));
/** CW-001 runtime branding on the client shell. Logo URL is client-safe or null (letter mark). */
export const ClientHubBrandingV1Schema = z
    .object({
    schemaVersion: z.literal("client-workspace-branding.v1.0"),
    requirementId: z.literal("CW-001"),
    ok: z.literal(true),
    businessDisplayName: z.string().min(1),
    agencyDisplayName: z.string().min(1),
    logoUrl: z.string().url().nullable(),
    helpLabel: z.string().min(1),
    primaryColor: ClientHubPrimaryColorSchema,
})
    .strict();
export const ClientPortalShellViewV1Schema = z
    .object({
    schemaId: z.literal("client-portal.shellView.v1"),
    clientLinkId: ClientLinkIdSchema,
    state: ClientLinkStateV1Schema,
    project: ClientSafeProjectViewV1Schema,
    headline: z.string().min(1),
    subcopy: z.string().min(1),
    sections: z.array(ClientPortalShellSectionV1Schema).min(1),
    projectWorkflow: z.enum(["DEFAULT", "EXTERNAL_PROOF"]).optional(),
    branding: ClientHubBrandingV1Schema.optional(),
})
    .strict();
export function approvalBindsToLatestPassingPreview(approval, latest) {
    if (!latest)
        return false;
    return (approval.runId === latest.runId &&
        approval.businessGraphRevisionId === latest.businessGraphRevisionId &&
        approval.artifactSetHash === latest.artifactSetHash);
}
export function freezeApprovedClientPreviewVersionId(input) {
    return computePreviewVersionId({
        agencyId: input.agencyId,
        projectId: input.projectId,
        runId: input.approval.runId,
        artifactSetHash: input.approval.artifactSetHash,
        exportBundleHash: input.approval.exportBundleHash ?? "",
        approvedArtifactUpdatedAtIso: input.approval.approvedArtifactUpdatedAtIso,
        previewRoute: input.previewRoute ?? "/",
    });
}
//# sourceMappingURL=client-link.js.map